// The tools of the MCP server of Nexxt (ADR-262 point 10, ADR-264), as data: what each one is called, what it says to
// an assistant and to the person who reads it in Claude, its area and the permissions that open it. `gateway-mcp`
// binds each one to the operation of its domain, with the input schema of the route it stands for; Backstage shows,
// for a role or a connection, which tools its permissions open. A tool is never more than its route: the domain checks
// the permission, the presenters hide the fields, the RLS filters the organizations.

/** The areas of the tools, as Backstage groups them. */
export const mcpToolAreas = [
  "account",
  "sales",
  "procurement",
  "catalog",
  "logistics",
  "documents",
  "integration",
  "worklist",
  "party",
  "crm",
] as const;
export type McpToolArea = (typeof mcpToolAreas)[number];

/** A page of a tool: what an assistant gets when it does not say, and the most it may ask (ADR-264). */
export const MCP_PAGE_DEFAULT = 25;
export const MCP_PAGE_MAX = 50;

/**
 * The longest answer of a tool, in characters of its JSON: about 20,000 tokens, under the 25,000 Claude Code takes
 * from a tool by default. A longer one is refused with what to ask instead, never cut.
 */
export const MCP_RESULT_MAX_CHARS = 60_000;

/** The tag around a text written outside Tomorrow (brands, suppliers, customers) in the answer of a tool. */
export const UNTRUSTED_TAG = "untrusted-text";

/**
 * What a prepared action acts on (ADR-265): the entity whose page Backstage links from the confirmation, and whose
 * label (a number, a name) the tool read when it prepared it.
 */
export const preparedActionSubjects = ["purchase_order", "inbound", "sales_order", "document", "linesheet"] as const;
export type PreparedActionSubject = (typeof preparedActionSubjects)[number];

/**
 * How long a prepared action waits for its holder in Backstage (ADR-265): a day, then it is expired and the assistant
 * prepares it again if it is still wanted.
 */
export const PREPARED_ACTION_HOURS = 24;

export interface McpToolInfo {
  /** snake_case, the name the assistant calls. */
  name: string;
  title: string;
  /** For the assistant, and for the person who reads it on the screen of Claude: what it gives, and when to use it. */
  description: string;
  area: McpToolArea;
  /** A read (P5.15c), or a write of the same route of Backstage (P5.15d, ADR-265). */
  kind: "read" | "write";
  /**
   * A write that cannot be undone (spec v0.3 §8.O, D-29): a confirmation of a PO, a lock or a cancellation, a document,
   * a mail to an external, a release to the warehouse. Through MCP the tool only prepares it, and its holder confirms
   * it in Backstage (ADR-039, ADR-265): `needsConfirmation`.
   */
  irreversible?: true;
  /** MCP annotations of a write: it may overwrite or remove what is there (else it only adds). */
  destructive?: boolean;
  /** MCP annotations of a write: doing it again with the same arguments changes nothing more. */
  idempotent?: boolean;
  /** An irreversible write on an entity that exists: the one its confirmation page links. */
  subject?: PreparedActionSubject;
  /**
   * An irreversible write: the route of Backstage whose operation its confirmation runs, the same in `gateway-mcp`
   * (which validates the arguments with its schema) and in `gateway-backstage` (which runs it).
   */
  route?: string;
  /**
   * The permissions that open the tool, any of them: the one of its route, or for an open route that each domain
   * filters (a search, the queues) those of the lists it reads. Empty only for a tool that reads the Actor alone, or
   * what its own access prepared.
   */
  permissions: readonly string[];
}

/**
 * The sentence every tool of the email templates of Guestlist ends with (ADR-287 §4, ADR-293): the tools write drafts,
 * a send is always a person's.
 */
const NO_SEND = " No tool sends an email: a send is approved by a person in Guestlist.";

/** The sentence every prepared write ends with: the assistant tells the user, never that it is done. */
const PREPARED =
  " Nothing changes yet: the tool prepares the action and answers a link where the user confirms it in Backstage. " +
  "Give the user the link, say what you prepared, and check it with get_prepared_action.";

export const mcpTools = [
  {
    name: "whoami",
    title: "Who am I in Nexxt",
    description:
      "Tells who you are acting as in Nexxt, the B2B portal of Tomorrow: the user, the connection or token in use, and " +
      "the permissions it carries now (those chosen for it that the user's role still delegates). Use it to check the " +
      "connection, or before a task, to know what you may read or change.",
    area: "account",
    kind: "read",
    permissions: [],
  },
  // --- Sales (M6, M7, M10) ---
  {
    name: "search_sales_orders",
    title: "Search sales orders",
    description:
      "Finds sales orders by a text of at least 2 characters: their number in Nexxt (SO-…) or on Joor. Returns " +
      "hits with the id to pass to get_sales_order. Use it when the user names an order.",
    area: "sales",
    kind: "read",
    permissions: ["sales_order.read"],
  },
  {
    name: "list_sales_orders",
    title: "List sales orders",
    description:
      "The sales orders of Tomorrow's customers, newest change first, with status, customer, brand, season, totals " +
      "and how each stands towards Business Central (bcSyncStatus). Filters make the queues of the Customer Service " +
      "and Credit Control: lockable, creditBlocked, awaitingPayment, depositDue, fulfillmentIssue, uncovered, " +
      "withoutConfirmation, confirmation. Brand and customer filters take ids (uuid): find them with search_parties.",
    area: "sales",
    kind: "read",
    permissions: ["sales_order.read"],
  },
  {
    name: "get_sales_order",
    title: "Get a sales order",
    description:
      "One sales order by its id or its number (SO-…): header, customer, brand, season, terms, totals, status and " +
      "how it stands on Business Central. Its lines come from list_sales_order_lines.",
    area: "sales",
    kind: "read",
    permissions: ["sales_order.read"],
  },
  {
    name: "list_sales_order_lines",
    title: "List the lines of a sales order",
    description:
      "The lines of a sales order by line number: SKU, style, colour, size, units ordered, allocated and shipped, " +
      "prices and status.",
    area: "sales",
    kind: "read",
    permissions: ["sales_order.read"],
  },
  {
    name: "get_sales_order_history",
    title: "History of a sales order",
    description:
      "Who did what on a sales order and its lines, newest first: each step and each change with its fields, and " +
      "whether it came from the app, a token or an assistant.",
    area: "sales",
    kind: "read",
    permissions: ["sales_order.read"],
  },
  {
    name: "get_sales_order_sync",
    title: "Sales order on Business Central",
    description:
      "How a sales order stands towards Business Central: the last state sent, its parts, the errors line by line " +
      "and whether it can be sent again. Use it when an order is sync_failed or the user asks why BC does not have it.",
    area: "sales",
    kind: "read",
    permissions: ["sales_order.manage"],
  },
  {
    name: "list_sales_order_totals",
    title: "Sales order totals",
    description:
      "The sales orders counted by season, brand, customer, status and currency, with units ordered, allocated and " +
      "shipped and their value after discounts (cancelled lines out). Use it for questions like 'how much did we " +
      "sell of a brand this season', instead of reading every order.",
    area: "sales",
    kind: "read",
    permissions: ["sales_order.read"],
  },
  // --- Procurement (M5, M11) ---
  {
    name: "search_purchase_orders",
    title: "Search purchase orders",
    description:
      "Finds purchase orders by a text of at least 2 characters, such as their number (PO-…). Returns hits with the " +
      "id to pass to get_purchase_order.",
    area: "procurement",
    kind: "read",
    permissions: ["purchase_order.read"],
  },
  {
    name: "list_purchase_orders",
    title: "List purchase orders",
    description:
      "The purchase orders to brands and suppliers, newest change first, with status, brand, supplier, season, " +
      "delivery window, totals and how each stands on Business Central. pendingChanges: the supplier proposed " +
      "changes that wait for Tomorrow; remindersOver: published and still not confirmed after the reminders. Brand " +
      "and supplier filters take ids (uuid): find them with search_parties.",
    area: "procurement",
    kind: "read",
    permissions: ["purchase_order.read"],
  },
  {
    name: "get_purchase_order",
    title: "Get a purchase order",
    description:
      "One purchase order by its id or its number (PO-…), with its totals, status and delivery window. Its lines come " +
      "from list_purchase_order_lines.",
    area: "procurement",
    kind: "read",
    permissions: ["purchase_order.read"],
  },
  {
    name: "list_purchase_order_lines",
    title: "List the lines of a purchase order",
    description:
      "The lines of a purchase order by line number: SKU, style, colour, size, units, costs (when your role sees " +
      "them) and status.",
    area: "procurement",
    kind: "read",
    permissions: ["purchase_order.read"],
  },
  {
    name: "list_purchase_order_changes",
    title: "Changes proposed on a purchase order",
    description:
      "What the supplier proposed on a purchase order (other units, dates or prices), newest first: those that wait " +
      "for Tomorrow and those already decided, with the supplier's reason.",
    area: "procurement",
    kind: "read",
    permissions: ["purchase_order.read"],
  },
  {
    name: "get_purchase_order_sync",
    title: "Purchase order on Business Central",
    description:
      "How a purchase order stands towards Business Central: the last state sent, its messages and errors line by " +
      "line, and whether it can be sent again.",
    area: "procurement",
    kind: "read",
    permissions: ["purchase_order.manage"],
  },
  {
    name: "list_inbounds",
    title: "List inbounds",
    description:
      "The inbounds, the goods suppliers announce against purchase orders, newest change first, with status, PO, " +
      "brand, supplier and expected delivery. toCapture: the packing list waits for Logistics; toResolve: beyond the " +
      "tolerance or with a discrepancy.",
    area: "procurement",
    kind: "read",
    permissions: ["inbound.read"],
  },
  {
    name: "get_inbound",
    title: "Get an inbound",
    description: "One inbound by its id or its number, with its lines, its documents and its status.",
    area: "procurement",
    kind: "read",
    permissions: ["inbound.read"],
  },
  {
    name: "list_inbound_notes",
    title: "Notes of an inbound",
    description: "The notes that the supplier, the brand and Tomorrow wrote on an inbound, oldest first.",
    area: "procurement",
    kind: "read",
    permissions: ["inbound.read"],
  },
  {
    name: "get_inbound_sync",
    title: "Inbound on the warehouse and Business Central",
    description:
      "How an inbound stands towards the warehouse and Business Central, message by message, with the errors.",
    area: "procurement",
    kind: "read",
    permissions: ["inbound.read"],
  },
  {
    name: "list_inbound_progress",
    title: "Goods coming in",
    description:
      "The purchase orders coming in, the earliest delivery window first: units ordered, announced, in transit and " +
      "received, with the stage and the discrepancies. Use it for 'what is arriving and what is late'.",
    area: "procurement",
    kind: "read",
    permissions: ["inbound.read"],
  },
  // --- Catalog (M2, M3, M4) ---
  {
    name: "search_catalog",
    title: "Search the catalog",
    description:
      "Finds linesheets, products and SKUs by a text of at least 2 characters: a name, a style code, an EAN. Returns " +
      "hits by type with the id to pass to get_linesheet or get_product.",
    area: "catalog",
    kind: "read",
    permissions: ["linesheet.read", "product.read"],
  },
  {
    name: "list_linesheets",
    title: "List linesheets",
    description:
      "The linesheets of the brands, newest change first, with brand, season, status, price status and how each " +
      "stands on Business Central and Joor. status pending: submitted by the brand, waits for Operations; " +
      "priceStatus pending: prices to complete.",
    area: "catalog",
    kind: "read",
    permissions: ["linesheet.read"],
  },
  {
    name: "get_linesheet",
    title: "Get a linesheet",
    description: "The header of a linesheet by its id or its name; its lines come from list_linesheet_lines.",
    area: "catalog",
    kind: "read",
    permissions: ["linesheet.read"],
  },
  {
    name: "list_linesheet_lines",
    title: "List the lines of a linesheet",
    description:
      "The lines of a linesheet by position: style, colour, sizes, prices (when your role sees them) and what each " +
      "line still misses. onlyIssues: only the lines with something missing.",
    area: "catalog",
    kind: "read",
    permissions: ["linesheet.read"],
  },
  {
    name: "list_linesheet_sync_runs",
    title: "Sync runs of a linesheet",
    description:
      "The runs that sent a linesheet to Business Central and Joor, newest first, with their outcome and how many " +
      "rows failed.",
    area: "catalog",
    kind: "read",
    permissions: ["linesheet.read"],
  },
  {
    name: "get_linesheet_erp_status",
    title: "Linesheet on the ERP",
    description:
      "The ERP completeness of a linesheet and where each SKU stands with the ERP of each company: sent, to send " +
      "again, or missing a field the file needs.",
    area: "catalog",
    kind: "read",
    permissions: ["linesheet.read"],
  },
  {
    name: "get_product",
    title: "Get a product",
    description: "A product (a style) with its SKUs and the linesheets it is in.",
    area: "catalog",
    kind: "read",
    permissions: ["product.read"],
  },
  {
    name: "get_sku_by_ean",
    title: "Find an SKU by EAN",
    description: "An SKU by its EAN (8 to 14 digits), with its product and customs attributes.",
    area: "catalog",
    kind: "read",
    permissions: ["product.read"],
  },
  // --- Logistics (M8, M12, M13) ---
  {
    name: "list_availability",
    title: "Stock availability",
    description:
      "The availability the warehouse system (IMS) last sent, by SKU and warehouse, with its time. Nexxt does not " +
      "hold stock: this is the IMS's figure. Filter by SKU code, warehouse or a text.",
    area: "logistics",
    kind: "read",
    permissions: ["stock.read"],
  },
  {
    name: "list_fulfillments",
    title: "List shipments of sales orders",
    description:
      "The shipments of the sales orders, newest first, with status, warehouse, order and how each stands with the " +
      "warehouse. ackOverdue: released and not acknowledged in time.",
    area: "logistics",
    kind: "read",
    permissions: ["fulfillment.read"],
  },
  {
    name: "get_fulfillment",
    title: "Get a shipment",
    description: "A shipment of a sales order with its lines and the units the warehouse shipped of each.",
    area: "logistics",
    kind: "read",
    permissions: ["fulfillment.read"],
  },
  {
    name: "get_fulfillment_sync",
    title: "Shipment on the warehouse",
    description:
      "How a shipment stands towards the warehouse: the last state sent, its errors and whether it can be sent again.",
    area: "logistics",
    kind: "read",
    permissions: ["fulfillment.read"],
  },
  // --- Documents (M9) ---
  {
    name: "list_documents",
    title: "List documents",
    description:
      "The documents of the sales (order confirmations, proformas, invoices, credit notes, delivery notes), newest " +
      "change first, with type, status, number, order and customer. retryable: failed and to ask again; overdue: " +
      "numbered and the PDF late.",
    area: "documents",
    kind: "read",
    permissions: ["document.read"],
  },
  {
    name: "get_document",
    title: "Get a document",
    description: "A document with every time it was asked for and how each went.",
    area: "documents",
    kind: "read",
    permissions: ["document.read"],
  },
  // --- Integration: the state towards Business Central (M16) ---
  {
    name: "list_bc_reconciliation_issues",
    title: "Mismatches with Business Central",
    description:
      "What the hourly check finds wrong between Nexxt and Business Central, latest first: an entity never sent, an " +
      "outcome not applied, one taken without its id. Use it for 'is everything on BC?'.",
    area: "integration",
    kind: "read",
    permissions: ["integration.read"],
  },
  {
    name: "list_integration_errors",
    title: "Integration error queue",
    description:
      "The error queue of the integrations, latest first: messages dead, failed, without an outcome in time, or " +
      "stalled, with the cause and the attempts. Never the bodies of the messages.",
    area: "integration",
    kind: "read",
    permissions: ["integration.read"],
  },
  // --- Work queues (home "Now") ---
  {
    name: "count_work_queues",
    title: "Count the work queues",
    description:
      "How many items wait in each queue asked, and since when the oldest: the numbers of the home 'Now' of " +
      "Backstage (orders to lock, POs to approve, documents to retry…). Queues your permissions do not cover come " +
      "back in failed. Then read the items with the list tool of the queue's domain and its filter.",
    area: "worklist",
    kind: "read",
    permissions: [
      "linesheet.read",
      "attributes.read",
      "purchase_order.read",
      "inbound.read",
      "sales_order.read",
      "document.read",
      "integration.read",
      "erp_file.queue",
      "stock.read",
      "fulfillment.read",
      "movement.read",
      "vendor_return.read",
      "organization.read",
      "etaf.read",
      "user.read",
      "gate.read",
    ],
  },
  // --- Address book (party) ---
  {
    name: "search_parties",
    title: "Search brands, suppliers and customers",
    description:
      "Finds organizations (brands, suppliers, customers), contacts, manufacturers and users by a text of at least 2 " +
      "characters. Returns hits with the id other tools take as a filter (brand, supplier, customer).",
    area: "party",
    kind: "read",
    permissions: ["organization.read", "etaf.read", "user.read"],
  },
  // --- Writes (P5.15d, ADR-265): the same routes of Backstage, with the version the assistant read ---
  {
    name: "get_prepared_action",
    title: "Check a prepared action",
    description:
      "Where an action you prepared stands: waiting for the user in Backstage (pending), confirmed with its outcome, " +
      "failed with the reason, discarded or expired. Only the actions this connection or token prepared.",
    area: "account",
    kind: "read",
    permissions: [],
  },
  {
    name: "save_purchase_order_lines",
    title: "Change lines of a purchase order",
    description:
      "Changes lines of a PO still generated (before its approval), all or none: units, unit cost, expected date, " +
      "vendor code, description, or cancels a line (status cancelled, with closureReason) and opens it again. Pass " +
      "the version of the PO from get_purchase_order and the ids of the lines from list_purchase_order_lines.",
    area: "procurement",
    kind: "write",
    destructive: true,
    idempotent: true,
    permissions: ["purchase_order.manage"],
  },
  {
    name: "add_inbound_note",
    title: "Write a note on an inbound",
    description:
      "Writes a note on an inbound, as the user: Logistics, Operations and the supplier read it in the notes of the " +
      "inbound. Use it to ask or record something about a shipment; no email is sent.",
    area: "procurement",
    kind: "write",
    destructive: false,
    idempotent: false,
    permissions: ["inbound.comment"],
  },
  {
    name: "track_inbound",
    title: "Write the tracking of an inbound",
    description:
      "Writes the tracking of an inbound on its way, all of it at once: carrier, pickup date and tracking numbers " +
      "(null and an empty list clear them). Pass the version of the inbound from get_inbound.",
    area: "procurement",
    kind: "write",
    destructive: true,
    idempotent: true,
    permissions: ["inbound.track"],
  },
  {
    name: "confirm_purchase_order",
    title: "Confirm a purchase order",
    description:
      "Prepares the confirmation of a published PO on the supplier's side: confirmed, it goes to Business Central " +
      "and cannot be taken back. Pass the version of the PO from get_purchase_order." +
      PREPARED,
    area: "procurement",
    kind: "write",
    irreversible: true,
    route: "POST /procurement/purchase-orders/{purchaseOrderId}/confirm",
    subject: "purchase_order",
    destructive: false,
    idempotent: true,
    permissions: ["purchase_order.confirm"],
  },
  {
    name: "publish_purchase_order",
    title: "Publish a purchase order to its supplier",
    description:
      "Prepares the publication of an approved PO: from then on the supplier sees it and is told. Pass the version of " +
      "the PO from get_purchase_order." +
      PREPARED,
    area: "procurement",
    kind: "write",
    irreversible: true,
    route: "POST /procurement/purchase-orders/{purchaseOrderId}/publish",
    subject: "purchase_order",
    destructive: false,
    idempotent: true,
    permissions: ["purchase_order.manage"],
  },
  {
    name: "cancel_purchase_order",
    title: "Cancel a purchase order",
    description:
      "Prepares the cancellation of a PO not yet confirmed, with the reason. Pass the version of the PO from " +
      "get_purchase_order." +
      PREPARED,
    area: "procurement",
    kind: "write",
    irreversible: true,
    route: "POST /procurement/purchase-orders/{purchaseOrderId}/cancel",
    subject: "purchase_order",
    destructive: false,
    idempotent: true,
    permissions: ["purchase_order.manage"],
  },
  {
    name: "dispatch_inbound",
    title: "Send an inbound to the warehouse",
    description:
      "Prepares the release of a checked inbound to the warehouse and to Business Central, which then wait for the " +
      "goods. Pass the version of the inbound from get_inbound." +
      PREPARED,
    area: "procurement",
    kind: "write",
    irreversible: true,
    route: "POST /procurement/inbounds/{inboundId}/dispatch",
    subject: "inbound",
    destructive: false,
    idempotent: true,
    permissions: ["inbound.check"],
  },
  {
    name: "correct_sales_order_allocation",
    title: "Correct the allocation of a sales order",
    description:
      "Sets the units each line of a sales order has committed, more or fewer than now, with an optional reason: " +
      "the correction of the Customer Service before the lock. Pass the version of the order from get_sales_order " +
      "and the ids of the lines from list_sales_order_lines.",
    area: "sales",
    kind: "write",
    destructive: true,
    idempotent: true,
    permissions: ["allocation.manage"],
  },
  {
    name: "request_allocation_run",
    title: "Run the allocation of a warehouse",
    description:
      "Asks for a run of the allocation of a warehouse: it ranks the open orders and proposes what each can take; " +
      "nothing is committed until someone confirms the run in Backstage. The run starts in the background.",
    area: "sales",
    kind: "write",
    destructive: false,
    idempotent: false,
    permissions: ["allocation.run"],
  },
  {
    name: "lock_sales_order",
    title: "Lock a sales order",
    description:
      "Prepares the lock of what is allocated to a sales order, in a lot shipped on its own: it goes to Business " +
      "Central and the warehouse. Pass the version of the order from get_sales_order." +
      PREPARED,
    area: "sales",
    kind: "write",
    irreversible: true,
    route: "POST /sales/sales-orders/{salesOrderId}/lock",
    subject: "sales_order",
    destructive: false,
    idempotent: true,
    permissions: ["sales_order.manage"],
  },
  {
    name: "cancel_sales_order",
    title: "Cancel a sales order",
    description:
      "Prepares the cancellation of a sales order not yet allocated, or one waiting for its payment, with the " +
      "reason. Pass the version of the order from get_sales_order." +
      PREPARED,
    area: "sales",
    kind: "write",
    irreversible: true,
    route: "POST /sales/sales-orders/{salesOrderId}/cancel",
    subject: "sales_order",
    destructive: false,
    idempotent: true,
    permissions: ["sales_order.manage"],
  },
  {
    name: "update_linesheet",
    title: "Change the header of a linesheet",
    description:
      "Changes the header of a linesheet: name, season, category, sales campaign, delivery windows, whether it goes " +
      "to Business Central and Joor. An absent field stays, null empties it. Pass the version from get_linesheet.",
    area: "catalog",
    kind: "write",
    destructive: true,
    idempotent: true,
    permissions: ["linesheet.write"],
  },
  {
    name: "request_linesheet_sync",
    title: "Send a linesheet to Business Central and Joor",
    description:
      "Prepares the sync of an approved linesheet: its items go to Business Central, then to Joor when the " +
      "linesheet says so. Pass the version from get_linesheet; mode update (default), overwrite or " +
      "only_missing_styles." +
      PREPARED,
    area: "catalog",
    kind: "write",
    irreversible: true,
    route: "POST /catalog/linesheets/{linesheetId}/sync",
    subject: "linesheet",
    destructive: false,
    idempotent: true,
    permissions: ["linesheet.sync"],
  },
  {
    name: "request_stock_movement",
    title: "Move units between warehouses",
    description:
      "Prepares a transfer of SKUs from a warehouse to another, with why (memo): confirmed, it goes to the " +
      "warehouse. SKU and warehouse ids come from list_availability." +
      PREPARED,
    area: "logistics",
    kind: "write",
    irreversible: true,
    route: "POST /logistics/stock-movements",
    destructive: false,
    idempotent: true,
    permissions: ["movement.request"],
  },
  {
    name: "resolve_availability_mismatches",
    title: "Mark availability mismatches checked",
    description:
      "Marks checked the mismatches between Nexxt and the IMS that Logistics looked into, at most 200 at once: they " +
      "leave the queue. Their ids come from the queue of the mismatches in Backstage or from the user.",
    area: "logistics",
    kind: "write",
    destructive: false,
    idempotent: true,
    permissions: ["stock.mismatch_resolve"],
  },
  {
    name: "retry_document",
    title: "Ask Business Central again for a document",
    description:
      "Prepares a new request of a document in error, or late, with the same key: Business Central makes it once, " +
      "and it may be numbered and sent. Pass the version of the document from get_document." +
      PREPARED,
    area: "documents",
    kind: "write",
    irreversible: true,
    route: "POST /documents/documents/{documentId}/retry",
    subject: "document",
    destructive: false,
    idempotent: true,
    permissions: ["document.retry"],
  },
  {
    name: "retry_integration_errors",
    title: "Retry messages of the error queue",
    description:
      "Sends again rows of the error queue (dead) to the domains that gave up on them, at most 100 at once: the " +
      "domains apply each one once. Their ids come from list_integration_errors.",
    area: "integration",
    kind: "write",
    destructive: false,
    idempotent: true,
    permissions: ["integration.retry"],
  },
  // --- Email templates of Guestlist (ADR-287 §3–§4, ADR-293): drafts written at once, never a send ---
  {
    name: "list_email_templates",
    title: "List email templates",
    description:
      "The email templates of Guestlist, edited last first, without their content: name, brand (null is Tomorrow), " +
      "languages, the version published and whether the draft changed since. Filter by part of the name or by brand." +
      NO_SEND,
    area: "crm",
    kind: "read",
    permissions: ["crm.template.read"],
  },
  {
    name: "get_email_template",
    title: "Get an email template",
    description:
      "One template with the draft document of each language: sections of columns of blocks, each with a stable id. " +
      "Read it before an edit: every edit names a block, a column or a section by its id. Texts are inline Markdown " +
      "(**bold**, *italic*, [label](https://…)) with fields as {{contact.firstName}}, each with its fallback in merge.",
    area: "crm",
    kind: "read",
    permissions: ["crm.template.read"],
  },
  {
    name: "validate_email_template",
    title: "Check an email template",
    description:
      "The checks of the draft (or, with published, of the version a send would take), for every language or one: " +
      "path, rule, message and what was expected. An error stops publish_email_template, a warning only says. The " +
      "draft saves even with errors, so check after a set of edits and correct them.",
    area: "crm",
    kind: "read",
    permissions: ["crm.template.read"],
  },
  {
    name: "render_email_preview",
    title: "Preview an email",
    description:
      "The email of one language as a sample recipient gets it (never a real contact): subject, preheader, the plain " +
      "text, the checks with the weight of the HTML, the blocks the sample does not see. Ask without html: the " +
      "document with its images inline is large, and meant for the editor of Guestlist.",
    area: "crm",
    kind: "read",
    permissions: ["crm.template.read"],
  },
  {
    name: "list_brand_kits",
    title: "List brand kits",
    description:
      "The brand kits an email is drawn with, Tomorrow's first: colours as tokens, light and dark, the font and " +
      "the logo. A document names its kit by id (themeId); the kits are changed by people in Guestlist.",
    area: "crm",
    kind: "read",
    permissions: ["crm.template.read"],
  },
  {
    name: "get_brand_kit",
    title: "Get a brand kit",
    description: "One brand kit by its id (themeId of a document): its colour tokens, light and dark, font and logo.",
    area: "crm",
    kind: "read",
    permissions: ["crm.template.read"],
  },
  {
    name: "create_email_template",
    title: "Create an email template",
    description:
      "A new template with the draft of its first language, for a brand or for Tomorrow, drawn with a brand kit " +
      "(list_brand_kits). Then add blocks with add_email_block." +
      NO_SEND,
    area: "crm",
    kind: "write",
    destructive: false,
    idempotent: false,
    permissions: ["crm.template.write"],
  },
  {
    name: "update_email_template_settings",
    title: "Change the settings of an email template",
    description:
      "The name and the default language of a template, and of the document of one language its subject, " +
      "preheader, brand kit and the fallbacks of its fields (merge, replaced as a whole). An absent field stays." +
      NO_SEND,
    area: "crm",
    kind: "write",
    destructive: true,
    idempotent: true,
    permissions: ["crm.template.write"],
  },
  {
    name: "add_email_block",
    title: "Add a block to an email",
    description:
      "Adds a block to the document of one language: into a column at a position, right after a block, or into a " +
      "new section (the default, at the end). Answers the id of the new block and the document as it is now. Ids " +
      "come from get_email_template." +
      NO_SEND,
    area: "crm",
    kind: "write",
    destructive: false,
    idempotent: false,
    permissions: ["crm.template.write"],
  },
  {
    name: "update_email_block",
    title: "Change a block of an email",
    description:
      "Writes a block as a whole in the place of the one with its id: what you leave out goes away, so start from " +
      "the block as get_email_template gave it." +
      NO_SEND,
    area: "crm",
    kind: "write",
    destructive: true,
    idempotent: true,
    permissions: ["crm.template.write"],
  },
  {
    name: "move_email_block",
    title: "Move a block of an email",
    description:
      "Moves a block to a column, after another block or into a new section; a section left empty goes." + NO_SEND,
    area: "crm",
    kind: "write",
    destructive: false,
    idempotent: true,
    permissions: ["crm.template.write"],
  },
  {
    name: "remove_email_block",
    title: "Remove a block of an email",
    description:
      "Removes a block from the draft of one language; a published version keeps it, and a section left empty " +
      "goes." +
      NO_SEND,
    area: "crm",
    kind: "write",
    destructive: true,
    idempotent: true,
    permissions: ["crm.template.write"],
  },
  {
    name: "update_email_section",
    title: "Change a section of an email",
    description:
      "The background of a section and the widths of its columns, in order and adding up to 12: one width more is a " +
      "new empty column at the end, one less takes away the last column, refused if it still has blocks. A new " +
      "section is made with add_email_block and new_section." +
      NO_SEND,
    area: "crm",
    kind: "write",
    destructive: true,
    idempotent: true,
    permissions: ["crm.template.write"],
  },
  {
    name: "move_email_section",
    title: "Move a section of an email",
    description:
      "Moves a section with all its blocks to a position among the sections of the document of one language, " +
      "counted without it (0 is the top)." +
      NO_SEND,
    area: "crm",
    kind: "write",
    destructive: false,
    idempotent: true,
    permissions: ["crm.template.write"],
  },
  {
    name: "remove_email_section",
    title: "Remove a section of an email",
    description:
      "Removes a section with all its blocks from the draft of one language; a published version keeps it." + NO_SEND,
    area: "crm",
    kind: "write",
    destructive: true,
    idempotent: true,
    permissions: ["crm.template.write"],
  },
  {
    name: "replace_email_document",
    title: "Replace the document of an email",
    description:
      "Writes the whole document of one language, with the version of the template you read: it may undo what " +
      "someone else did since, so it is refused if the version changed. Prefer the edits of single blocks." +
      NO_SEND,
    area: "crm",
    kind: "write",
    destructive: true,
    idempotent: true,
    permissions: ["crm.template.write"],
  },
  {
    name: "add_email_locale",
    title: "Add a language to an email",
    description:
      "A new language, a copy of another (the default one) with the same block ids: then translate it block by " +
      "block with update_email_block." +
      NO_SEND,
    area: "crm",
    kind: "write",
    destructive: false,
    idempotent: false,
    permissions: ["crm.template.write"],
  },
  {
    name: "publish_email_template",
    title: "Publish an email template",
    description:
      "Makes the draft the next published version, with its brand kits as they are now; refused while " +
      "validate_email_template has errors. Pass the version of the template you read. Publishing sends nothing: a " +
      "send takes a published version and is approved by a person in Guestlist.",
    area: "crm",
    kind: "write",
    destructive: false,
    idempotent: false,
    permissions: ["crm.template.write"],
  },
  {
    name: "archive_email_template",
    title: "Archive an email template",
    description:
      "Takes a template out of the lists and of every edit; it is never deleted. Pass the version you read." + NO_SEND,
    area: "crm",
    kind: "write",
    destructive: true,
    idempotent: true,
    permissions: ["crm.template.write"],
  },
  // --- Campaigns, accounts and Today of Guestlist (ADR-299, ADR-300): reads of the projection of crm, never a contact's
  // email or phone, never a booking link; inviting and sending links stay with the people in Guestlist (B-155) ---
  {
    name: "list_crm_campaigns",
    title: "List campaigns",
    description:
      "The campaigns of Tomorrow's showrooms, the latest to start first, each with how many customers are invited " +
      "and how many invitations stand at each stage (invited, booked, visited, no_show, declined, withdrawn). " +
      "Filter by part of the name; the archived ones apart.",
    area: "crm",
    kind: "read",
    permissions: ["crm.campaign.read"],
  },
  {
    name: "get_crm_campaign",
    title: "Get a campaign",
    description:
      "One campaign with its funnel and the brands its invitations are for, each with its own stages: the head and " +
      "the columns of the matrix of Guestlist.",
    area: "crm",
    kind: "read",
    permissions: ["crm.campaign.read"],
  },
  {
    name: "list_campaign_invitations",
    title: "List the invitations of a campaign",
    description:
      "The invited customers of a campaign by name, each with every invitation it has there: the brand, the stage, " +
      "when it was invited, the appointment. With stage or brandOrgId, the customers that have such an invitation " +
      "(e.g. still invited: not booked yet). People and their emails are not here.",
    area: "crm",
    kind: "read",
    permissions: ["crm.campaign.read"],
  },
  {
    name: "get_crm_today",
    title: "What to do today in Guestlist",
    description:
      "The home of Guestlist, of one sales territory or of all: the running campaigns with the invitations nobody " +
      "answered, the appointments of the next days, and, with the permission to read the accounts, the customers " +
      "quiet for a while. Territories from list_crm_territories.",
    area: "crm",
    kind: "read",
    permissions: ["crm.campaign.read"],
  },
  {
    name: "list_crm_accounts",
    title: "List accounts",
    description:
      "The accounts still in business by name, customers, brands or both, with country, sales territory and " +
      "customer code. Filter by part of the name, by kind or by territory (list_crm_territories).",
    area: "crm",
    kind: "read",
    permissions: ["crm.account.read"],
  },
  {
    name: "get_crm_account",
    title: "Get an account",
    description:
      "The card 360 of an account: its people by name and role (never their email or phone), its invitations " +
      "campaign by campaign and its latest orders, each part up to its limit with hasMore.",
    area: "crm",
    kind: "read",
    permissions: ["crm.account.read"],
  },
  {
    name: "list_crm_territories",
    title: "List sales territories",
    description: "The sales territories of the accounts still in business, by name: the values of their filter.",
    area: "crm",
    kind: "read",
    permissions: ["crm.account.read"],
  },
  // --- The signals of Guestlist (ADR-316 §7): counted, or by account, never a person's email, phone or what they did
  // one by one; the moves of Today, sending and writing an interest stay with a person in Guestlist ---
  {
    name: "get_account_engagement",
    title: "What an account did with Tomorrow's emails",
    description:
      "How warm an account is (hot, warm, cool, cold; its tags, the best time to reach it, the last season it booked) " +
      "and its human signals of the last 90 days: opens, clicks, booking links opened, times seen, bookings, in total, " +
      "by send and by campaign. Counts of the account, never who.",
    area: "crm",
    kind: "read",
    permissions: ["crm.account.read"],
  },
  {
    name: "list_hot_accounts",
    title: "Accounts warm right now",
    description:
      "The accounts whose buyers did something in the last 15 minutes (clicked an email, opened their booking link, " +
      "saw the times, booked), one line each, the newest first, at most 20, of one territory or all, through the lens " +
      'of some brands. Never who. For "who should I call today?": this, then get_crm_today, then list_crm_accounts ' +
      "sorted by warmth or with the tag link_opened_not_booked; suggest calls, never send or write for the user.",
    area: "crm",
    kind: "read",
    permissions: ["crm.account.read"],
  },
  {
    name: "get_crm_insights",
    title: "Insights of a season",
    description:
      "One view of Guestlist's Insights for a season (the latest started without seasonId) and one territory or all: " +
      "season (against the one before at the same day), first_season (buyers by the season of their first booking), " +
      "brands_territories (interest of each territory for each brand), arrivals (where the buyers come from). Counts " +
      "without persons.",
    area: "crm",
    kind: "read",
    permissions: ["crm.campaign.read"],
  },
  // --- The sends of Guestlist (ADR-319): a tool drafts, shows who a send reaches, asks its approval, cancels one and
  // sends a test to Tomorrow; approving, pausing and resuming stay with a person in Guestlist (ADR-287 §4) ---
  {
    name: "list_crm_sends",
    title: "List sends",
    description:
      "The sends of Guestlist, the latest first, each with its status (draft, awaiting_approval, approved, sending, " +
      "paused, sent, cancelled), when it leaves, its waves and how many were sent, left out and not delivered. " +
      "Filter by campaign or status.",
    area: "crm",
    kind: "read",
    permissions: ["crm.send.read"],
  },
  {
    name: "get_crm_send",
    title: "Get a send",
    description: "One send of Guestlist: its email and version, who it goes to, when, its status, waves and counts.",
    area: "crm",
    kind: "read",
    permissions: ["crm.send.read"],
  },
  {
    name: "get_crm_send_stats",
    title: "How a send did",
    description:
      "The report of a send: delivered, opened, clicked, booking links opened, times seen, booked, unsubscribed and " +
      "bounced, real people and everything recorded side by side, and its first 72 hours hour by hour. Never who " +
      "clicked: who is worth a call is in Guestlist.",
    area: "crm",
    kind: "read",
    permissions: ["crm.send.read"],
  },
  {
    name: "preview_send_audience",
    title: "Who a send reaches",
    description:
      "How many people a send reaches, who is left out and why, ten names with their account and language (never " +
      "how to reach them), and the deliveries by hour of its choice of when.",
    area: "crm",
    kind: "read",
    permissions: ["crm.send.read"],
  },
  {
    name: "create_send",
    title: "Draft a send",
    description:
      "A draft of a send of a published email (list_email_templates) to the people of a campaign " +
      "(list_crm_campaigns), with who and when. It keeps the version published now. Then preview_send_audience and " +
      "request_send_approval." +
      NO_SEND,
    area: "crm",
    kind: "write",
    destructive: false,
    idempotent: false,
    permissions: ["crm.send.write"],
  },
  {
    name: "update_send",
    title: "Change a draft send",
    description:
      "Changes the name, the email, who or when of a draft send, or of one waiting for its approval, which goes " +
      "back to a draft." +
      NO_SEND,
    area: "crm",
    kind: "write",
    destructive: true,
    idempotent: true,
    permissions: ["crm.send.write"],
  },
  {
    name: "request_send_approval",
    title: "Ask the approval of a send",
    description:
      "A draft send waits for a person to approve it in Guestlist, in three steps. Tell the user to approve it " +
      "there: no tool approves a send." +
      NO_SEND,
    area: "crm",
    kind: "write",
    destructive: false,
    idempotent: false,
    permissions: ["crm.send.write"],
  },
  {
    name: "cancel_send",
    title: "Cancel a send",
    description:
      "Cancels a send not finished yet: who has not received it will not. The emails already sent stay sent; it " +
      "cannot be undone, so do it only when the user asks.",
    area: "crm",
    kind: "write",
    destructive: true,
    idempotent: true,
    permissions: ["crm.send.write"],
  },
  {
    name: "send_test_email",
    title: "Send a test email",
    description:
      "An email of a template, its draft or its published version, to at most five addresses of Tomorrow, with a " +
      "sample recipient and nothing measured: never to a customer.",
    area: "crm",
    kind: "write",
    destructive: false,
    idempotent: false,
    permissions: ["crm.send.write"],
  },
] as const satisfies readonly McpToolInfo[];

export type McpToolName = (typeof mcpTools)[number]["name"];

/** The writes that are prepared through MCP and confirmed in Backstage (ADR-265). */
export type McpPreparedToolName = Extract<(typeof mcpTools)[number], { irreversible: true }>["name"];

/** The tools that write and are done at once, through the `Mcp<Domain>Service` of their domain. */
export type McpDirectWriteName = Exclude<
  Extract<(typeof mcpTools)[number], { kind: "write" }>["name"],
  McpPreparedToolName
>;

/**
 * Whether a write through MCP waits for its holder to confirm it in Backstage: every irreversible one, for every role
 * (spec v0.3 §8.O; D-29, B-110: at least until the end of March 2027). TODO(D-29): when the business decides that the
 * permission of a role is enough for some tools, the exceptions come from the delegation policy of the role, read with
 * the Actor, and this takes them; the confirmation in Backstage stays what a tool does without one.
 */
export const needsConfirmation = (tool: Pick<McpToolInfo, "irreversible">): boolean => tool.irreversible === true;

/** The tool of the catalog with that name, if there is one. */
export const mcpToolNamed = (name: string): McpToolInfo | undefined =>
  (mcpTools as readonly McpToolInfo[]).find((tool) => tool.name === name);

/** Whether the permissions open the tool: one of its own, or none needed. */
export const opensTool = (tool: Pick<McpToolInfo, "permissions">, permissions: readonly string[]): boolean =>
  tool.permissions.length === 0 || tool.permissions.some((p) => permissions.includes(p));

/** The tools the permissions open, in the order of the catalog. */
export const mcpToolsFor = (permissions: readonly string[]): McpToolInfo[] =>
  mcpTools.filter((tool) => opensTool(tool, permissions));
